import ko from "knockout";
import Clipboard from "clipboard";
import { uniqueId } from "lodash/fp";
import ViewModel from "viewModels/ViewModelTyped";
import toastService from "services/toastService";
import errorCallbackFactory from "util/errorCallbackFactory";
import Icons from "enums/Icons";

export interface CopyButtonParams {
    value: ko.Observable<string> | string;
    lString?: ko.Observable<string> | string;
    lAriaLabelKey?: ko.Observable<string> | string;
}

export default class CopyButton extends ViewModel {
    public readonly id: string;
    public readonly screenReaderText?: string;
    public readonly visible: ko.Computed<boolean>;
    public readonly Icons = Icons;
    private clipboard?: Clipboard;

    constructor(
        { value, lString, lAriaLabelKey }: CopyButtonParams,
        componentElement: Element
    ) {
        super();
        this.id = uniqueId("copy-button-");

        let ariaLabelKey = "components.copy-button.button.label.v4";

        // if aria label key is specified and exists, use it, otherwise use default
        if (lAriaLabelKey && ko.i18n.keyExists(ko.unwrap(lAriaLabelKey))) {
            ariaLabelKey = ko.unwrap(lAriaLabelKey);
        }

        this.screenReaderText = ko.i18n.renderString(ariaLabelKey, {
            text: ko.unwrap(value),
        });

        this.visible = this.computed(
            () => Clipboard.isSupported() && !!ko.unwrap(value)
        );

        if (Clipboard.isSupported()) {
            this.clipboard = new Clipboard(this.selector, {
                text: () => ko.unwrap(value),
                container: componentElement, // Use the component element as container
            });

            this.clipboard.on("success", ({ text }) => {
                // @ts-expect-error toastService is JS and notifyMessage has optional param useLongDelay
                toastService.notifyMessage(
                    ko.i18n.renderString(
                        lString || "components.copy-button.copySuccess",
                        {
                            text,
                        }
                    ),
                    { tag: this.selector, markdownToHtml: false }
                );
            });

            this.clipboard.on(
                "error",
                errorCallbackFactory.createCallback({
                    namespace: "components.copy-button",
                })
            );
        }
    }

    get selector(): string {
        return `#${this.id}`;
    }

    onDispose(): void {
        if (this.clipboard) {
            this.clipboard.destroy();
        }
    }
}
